/* eslint-disable no-param-reassign */
import { useMemo } from 'react'
import qs from 'qs'

import domainService from '@tabeeb/services/domainService'
import oidcUserManager from '@tabeeb/services/oidcUserManager'
import { Api as MainApi, HttpClient } from '@tabeeb/contracts/main-api/swagger-main-api'
import { Api as AiServiceApi } from '@tabeeb/contracts/microservices/swagger-ai-service'
import { Api as ArticleServiceApi } from '@tabeeb/contracts/microservices/swagger-article-service'
import { Api as AutodeskServiceApi } from '@tabeeb/contracts/microservices/swagger-autodesk-service'
import { Api as CertificateServiceApi } from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { Api as ChatBotServiceApi } from '@tabeeb/contracts/microservices/swagger-chat-bot-service'
import { Api as ContractorServiceApi } from '@tabeeb/contracts/microservices/swagger-contractor-service'
import { Api as DiscoveryServiceApi } from '@tabeeb/contracts/microservices/swagger-discovery-service'
import { Api as HealthDeviceServiceApi } from '@tabeeb/contracts/microservices/swagger-health-device-service'
import { Api as IdentityServiceApi } from '@tabeeb/contracts/identity-server/swagger-identity-service'
import { Api as ImportServiceApi } from '@tabeeb/contracts/microservices/swagger-import-service'
import { Api as NotificationsServiceApi } from '@tabeeb/contracts/microservices/swagger-notifications-service'
import { Api as ReportingServiceApi } from '@tabeeb/contracts/microservices/swagger-reporting-service'
import { Api as SalesforceIntegrationServiceApi } from '@tabeeb/contracts/microservices/swagger-salesforce-integration-service'
import { Api as SpreadsheetServiceApi } from '@tabeeb/contracts/microservices/swagger-spreadsheet-service'
import { Api as TenantServiceApi } from '@tabeeb/contracts/microservices/swagger-tenant-service'
import { Api as WorkflowServiceApi } from '@tabeeb/contracts/microservices/swagger-workflow-service'

const httpClient = new HttpClient<void>({
  baseURL: process.env.TABEEB_API_URL,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
})

httpClient.instance.interceptors.request.use(async (config) => {
  if (config?.headers) {
    const user = await oidcUserManager.getUser()
    if (user) {
      config.headers.Authorization = `Bearer ${user.access_token}`
    }

    if (!config.headers['X-Tenant-Subdomain']) {
      config.headers['X-Tenant-Subdomain'] = domainService.subdomain
    }
  }

  return config
})

const sharedHttpClient = httpClient as any

const identityServiceApi = new IdentityServiceApi(sharedHttpClient)

const mainApi = new MainApi(sharedHttpClient)

const aiServiceApi = new AiServiceApi(sharedHttpClient)
const articleServiceApi = new ArticleServiceApi(sharedHttpClient)
const autodeskServiceApi = new AutodeskServiceApi(sharedHttpClient)
const certificateServiceApi = new CertificateServiceApi(sharedHttpClient)
const chatBotServiceApi = new ChatBotServiceApi(sharedHttpClient)
const contractorServiceApi = new ContractorServiceApi(sharedHttpClient)
const discoveryServiceApi = new DiscoveryServiceApi(sharedHttpClient)
const healthDeviceServiceApi = new HealthDeviceServiceApi(sharedHttpClient)
const importServiceApi = new ImportServiceApi(sharedHttpClient)
const notificationsServiceApi = new NotificationsServiceApi(sharedHttpClient)
const reportingServiceApi = new ReportingServiceApi(sharedHttpClient)
const salesforceIntegrationServiceApi = new SalesforceIntegrationServiceApi(sharedHttpClient)
const spreadsheetServiceApi = new SpreadsheetServiceApi(sharedHttpClient)
const tenantServiceApi = new TenantServiceApi(sharedHttpClient)
const workflowServiceApi = new WorkflowServiceApi(sharedHttpClient)

const useApi = () => {
  return useMemo(
    () => ({
      identityServiceApi,
      mainApi,
      aiServiceApi,
      articleServiceApi,
      autodeskServiceApi,
      certificateServiceApi,
      chatBotServiceApi,
      contractorServiceApi,
      discoveryServiceApi,
      healthDeviceServiceApi,
      importServiceApi,
      notificationsServiceApi,
      reportingServiceApi,
      salesforceIntegrationServiceApi,
      spreadsheetServiceApi,
      tenantServiceApi,
      workflowServiceApi,
    }),
    []
  )
}

export default useApi
